// FTAShapes.tsx — Full AIAG-VDA + IEC 61025 FTA symbol library
// Covers: Events, Gates, Transfer, House, Inhibit symbols

interface ShapeProps { className?: string; fill?: string; stroke?: string; }

// ── Events ───────────────────────────────────────────────────────────────────

/** TOP EVENT — rounded rectangle (system-level failure) */
export const EventTop = ({ fill = '#fee2e2', stroke = '#dc2626' }: ShapeProps) => (
    <svg viewBox="0 0 120 70" className="w-full h-full">
        <rect x="2" y="2" width="116" height="66" rx="12" ry="12"
            fill={fill} stroke={stroke} strokeWidth="2.5" />
    </svg>
);

/** INTERMEDIATE EVENT — rectangle */
export const EventIntermediate = ({ fill = '#dbeafe', stroke = '#2563eb' }: ShapeProps) => (
    <svg viewBox="0 0 120 70" className="w-full h-full">
        <rect x="2" y="2" width="116" height="66"
            fill={fill} stroke={stroke} strokeWidth="2" />
    </svg>
);

/** BASIC EVENT — circle (primary failure cause) */
export const EventBasic = ({ fill = '#dcfce7', stroke = '#16a34a' }: ShapeProps) => (
    <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r="46"
            fill={fill} stroke={stroke} strokeWidth="2.5" />
    </svg>
);

/** UNDEVELOPED EVENT — diamond (not further analyzed) */
export const EventUndeveloped = ({ fill = '#fef9c3', stroke = '#ca8a04' }: ShapeProps) => (
    <svg viewBox="0 0 100 100" className="w-full h-full">
        <polygon points="50,3 97,50 50,97 3,50"
            fill={fill} stroke={stroke} strokeWidth="2.5" />
    </svg>
);

/** CONDITIONING EVENT — ellipse (condition/constraint) */
export const EventConditioning = ({ fill = '#f3e8ff', stroke = '#9333ea' }: ShapeProps) => (
    <svg viewBox="0 0 120 80" className="w-full h-full">
        <ellipse cx="60" cy="40" rx="57" ry="37"
            fill={fill} stroke={stroke} strokeWidth="2" />
    </svg>
);

/** HOUSE EVENT — house shape (switch / normally expected) */
export const EventHouse = ({ fill = '#e0f2fe', stroke = '#0284c7' }: ShapeProps) => (
    <svg viewBox="0 0 100 110" className="w-full h-full">
        <polygon points="50,5 95,45 95,105 5,105 5,45"
            fill={fill} stroke={stroke} strokeWidth="2.5" />
    </svg>
);

/** EXTERNAL EVENT — circle with X (external / not in system) */
export const EventExternal = ({ fill = '#f0fdf4', stroke = '#15803d' }: ShapeProps) => (
    <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r="46" fill={fill} stroke={stroke} strokeWidth="2.5" />
        <line x1="27" y1="27" x2="73" y2="73" stroke={stroke} strokeWidth="2.5" />
        <line x1="73" y1="27" x2="27" y2="73" stroke={stroke} strokeWidth="2.5" />
    </svg>
);

// ── Gates ────────────────────────────────────────────────────────────────────

/** AND GATE — flat bottom, dome top (IEC 61025 style) */
export const GateAND = ({ fill = '#dbeafe', stroke = '#1d4ed8' }: ShapeProps) => (
    <svg viewBox="0 0 100 90" className="w-full h-full">
        <path d="M 8 88 L 8 42 A 42 46 0 0 1 92 42 L 92 88 Z"
            fill={fill} stroke={stroke} strokeWidth="2.5" />
        <line x1="50" y1="88" x2="50" y2="100" stroke={stroke} strokeWidth="2" />
    </svg>
);

/** OR GATE — curved shape (IEC 61025 style) */
export const GateOR = ({ fill = '#fef3c7', stroke = '#d97706' }: ShapeProps) => (
    <svg viewBox="0 0 100 90" className="w-full h-full">
        <path d="M 8 88 Q 50 72 92 88 L 92 38 Q 92 5 50 5 Q 8 5 8 38 Z"
            fill={fill} stroke={stroke} strokeWidth="2.5" />
    </svg>
);

/** EXCLUSIVE OR (XOR) GATE */
export const GateXOR = ({ fill = '#fee2e2', stroke = '#dc2626' }: ShapeProps) => (
    <svg viewBox="0 0 100 100" className="w-full h-full">
        <path d="M 8 95 Q 50 78 92 95 L 92 40 Q 92 5 50 5 Q 8 5 8 40 Z"
            fill={fill} stroke={stroke} strokeWidth="2.5" />
        {/* XOR arc */}
        <path d="M 8 78 Q 50 62 92 78" fill="none" stroke={stroke} strokeWidth="2.5" />
    </svg>
);

/** INHIBIT GATE — hexagon/parallelogram (conditional AND) */
export const GateINHIBIT = ({ fill = '#f5f3ff', stroke = '#7c3aed' }: ShapeProps) => (
    <svg viewBox="0 0 100 90" className="w-full h-full">
        <polygon points="50,2 92,25 92,72 50,88 8,72 8,25"
            fill={fill} stroke={stroke} strokeWidth="2.5" />
        <text x="50" y="52" textAnchor="middle" fontSize="12" fill={stroke} fontWeight="bold">INHIBIT</text>
    </svg>
);

/** PRIORITY AND GATE — AND gate with triangle inside */
export const GatePAND = ({ fill = '#ecfdf5', stroke = '#059669' }: ShapeProps) => (
    <svg viewBox="0 0 100 90" className="w-full h-full">
        <path d="M 8 88 L 8 42 A 42 46 0 0 1 92 42 L 92 88 Z"
            fill={fill} stroke={stroke} strokeWidth="2.5" />
        <polygon points="50,48 65,72 35,72"
            fill={stroke} stroke={stroke} strokeWidth="1" />
    </svg>
);

// ── Transfer / Special ────────────────────────────────────────────────────────

/** TRANSFER IN — triangle pointing up */
export const TransferIn = ({ fill = '#f8fafc', stroke = '#475569' }: ShapeProps) => (
    <svg viewBox="0 0 100 90" className="w-full h-full">
        <polygon points="50,5 95,85 5,85"
            fill={fill} stroke={stroke} strokeWidth="2.5" />
        <circle cx="50" cy="85" r="8" fill={stroke} />
    </svg>
);

/** TRANSFER OUT — triangle pointing down */
export const TransferOut = ({ fill = '#f8fafc', stroke = '#475569' }: ShapeProps) => (
    <svg viewBox="0 0 100 90" className="w-full h-full">
        <polygon points="50,85 95,5 5,5"
            fill={fill} stroke={stroke} strokeWidth="2.5" />
        <circle cx="50" cy="5" r="8" fill={stroke} />
    </svg>
);

// ── Block Diagram Symbols (AIAG-VDA) ─────────────────────────────────────────

/** FUNCTION BLOCK — plain rectangle (AIAG-VDA Block Diagram) */
export const BlockFunction = ({ fill = '#eff6ff', stroke = '#3b82f6' }: ShapeProps) => (
    <svg viewBox="0 0 130 60" className="w-full h-full">
        <rect x="2" y="2" width="126" height="56"
            fill={fill} stroke={stroke} strokeWidth="2" />
    </svg>
);

/** PROCESS BLOCK — rectangle with inset line */
export const BlockProcess = ({ fill = '#f0fdf4', stroke = '#22c55e' }: ShapeProps) => (
    <svg viewBox="0 0 130 60" className="w-full h-full">
        <rect x="2" y="2" width="126" height="56" fill={fill} stroke={stroke} strokeWidth="2" />
        <rect x="8" y="8" width="114" height="44" fill="none" stroke={stroke} strokeWidth="1" strokeDasharray="3,2" />
    </svg>
);

/** DECISION / CONDITION — diamond */
export const BlockDecision = ({ fill = '#fefce8', stroke = '#eab308' }: ShapeProps) => (
    <svg viewBox="0 0 130 80" className="w-full h-full">
        <polygon points="65,3 127,40 65,77 3,40"
            fill={fill} stroke={stroke} strokeWidth="2" />
    </svg>
);

/** INTERFACE / CONNECTOR — circle */
export const BlockConnector = ({ fill = '#f0f9ff', stroke = '#0ea5e9' }: ShapeProps) => (
    <svg viewBox="0 0 80 80" className="w-full h-full">
        <circle cx="40" cy="40" r="37" fill={fill} stroke={stroke} strokeWidth="2" />
    </svg>
);
